import { useEffect, useState } from "react";
import { api, SALES_CHANNELS } from "@/lib/api";
import { toast } from "sonner";
import { Store, Loader2, Check, Save, ExternalLink, Info, RefreshCw, Workflow, Heart, Tag, Image as ImageIcon, X, Wrench, AlertTriangle, DollarSign, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

export default function Distribution() {
  const [settings, setSettings] = useState(null);
  const [counts, setCounts] = useState({});
  const [saving, setSaving] = useState(false);
  const [oauthEnabled, setOauthEnabled] = useState(false);
  const [shopifyConnected, setShopifyConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [liveChannels, setLiveChannels] = useState(null);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [branding, setBranding] = useState(null);
  const [applyingBranding, setApplyingBranding] = useState(false);
  const [refitting, setRefitting] = useState(false);
  const [trimming, setTrimming] = useState(false);
  const [trimProgress, setTrimProgress] = useState(null);
  const [gattrs, setGattrs] = useState(false);
  const [gattrsProgress, setGattrsProgress] = useState(null);
  const [fixImg, setFixImg] = useState(false);
  const [fixImgProgress, setFixImgProgress] = useState(null);
  const [mockupColor, setMockupColor] = useState("Black");
  const [colorOptions, setColorOptions] = useState([]);
  const [apparelColors, setApparelColors] = useState(6);
  const [apparelColorOptions, setApparelColorOptions] = useState([4, 6, 8]);
  const [publishFloor, setPublishFloor] = useState({ enabled: true, mult: 2.2, margin_enabled: true, margin_floor_pct: 25 });
  const [foldedBusy, setFoldedBusy] = useState(false);
  const [foldedProgress, setFoldedProgress] = useState(null);
  const [titleBusy, setTitleBusy] = useState(false);
  const [titleProgress, setTitleProgress] = useState(null);
  const [claimsBusy, setClaimsBusy] = useState(false);
  const [claimsProgress, setClaimsProgress] = useState(null);
  const [tmBusy, setTmBusy] = useState(false);
  const [singleBusy, setSingleBusy] = useState(false);
  const [textBusy, setTextBusy] = useState(false);
  const [textProgress, setTextProgress] = useState(null);
  const [singleProgress, setSingleProgress] = useState(null);
  const [tmProgress, setTmProgress] = useState(null);
  const [walmartBusy, setWalmartBusy] = useState(false);
  const [walmartProgress, setWalmartProgress] = useState(null);
  const [apparelBusy, setApparelBusy] = useState(false);
  const [apparelProgress, setApparelProgress] = useState(null);
  const [diagBusy, setDiagBusy] = useState(false);
  const [diag, setDiag] = useState(null);
  const [diagProgress, setDiagProgress] = useState(null);
  const [allAvailBusy, setAllAvailBusy] = useState(false);
  const [allAvailProgress, setAllAvailProgress] = useState(null);
  const [fixUrl, setFixUrl] = useState("");
  const [fixOneBusy, setFixOneBusy] = useState(false);
  const [fixOneResult, setFixOneResult] = useState(null);
  const [draftBusy, setDraftBusy] = useState(null);
  const [auditBusy, setAuditBusy] = useState(false);
  const [auditProgress, setAuditProgress] = useState(null);
  const [audit, setAudit] = useState(null);
  const [repairBusy, setRepairBusy] = useState(false);
  const [repairProgress, setRepairProgress] = useState(null);
  const [repairing, setRepairing] = useState(null); // shopify_id being piloted, or "bulk"
  const [stockReport, setStockReport] = useState(null);
  const [stockBusy, setStockBusy] = useState(false);
  const [showStockTable, setShowStockTable] = useState(false);
  const [hideBusy, setHideBusy] = useState(false);
  const [hideProgress, setHideProgress] = useState(null);
  const [restoreBusy, setRestoreBusy] = useState(false);
  const [stockDigest, setStockDigest] = useState(null);

  const draftProduct = async ({ url, productId, key }) => {
    if (!window.confirm("Move this product to Draft? It will fall off the Honorifica.com storefront but stay in Shopify/Printify (you can set it Active again or delete it later).")) return;
    setDraftBusy(key);
    try {
      const { data } = await api.post("/shopify/set-status", { url: url || "", product_id: productId || "", status: "draft" });
      toast.success(`Moved to Draft — ${data.title || "product"} will fall off the storefront`);
      if (productId) setDiag((d) => d ? { ...d, problems: (d.problems || []).filter((p) => p.shopify_id !== productId), problem_count: Math.max(0, (d.problem_count || 1) - 1) } : d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not set to draft");
    } finally {
      setDraftBusy(null);
    }
  };

  const fixOneProduct = async () => {
    setFixOneBusy(true);
    setFixOneResult(null);
    try {
      const { data } = await api.post("/shopify/fix-product", { url: fixUrl.trim() });
      setFixOneResult(data);
      toast[data.fixed ? "success" : "warning"](data.fixed ? "Product is now sellable" : "Ran, but still not fully sellable");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not fix that product");
    } finally {
      setFixOneBusy(false);
    }
  };

  const [jobIds, setJobIds] = useState({});
  const cancelJob = async (kind, jobId, setBusy) => {
    if (!jobId) { setBusy(false); return; }
    if (!window.confirm("Stop this running job?")) return;
    try {
      await api.post(`/jobs/${jobId}/cancel`);
      toast.success("Job cancelled");
    } catch (e) {
      toast.error("Could not cancel — it may have already finished");
    } finally {
      setBusy(false);
      setJobIds((s) => ({ ...s, [kind]: null }));
    }
  };


  const trackAllAvailJob = (jobId) => {
    setAllAvailBusy(true);
    setJobIds((s) => ({ ...s, allAvail: jobId }));
    const poll = setInterval(async () => {
      try {
        const { data: j } = await api.get(`/jobs/${jobId}`);
        setAllAvailProgress({ done: j.completed || 0, total: j.total || 0 });
        if (["done", "error", "cancelled"].includes(j.status)) {
          clearInterval(poll);
          setAllAvailBusy(false);
          setJobIds((s) => ({ ...s, allAvail: null }));
          if (j.status === "done") toast.success(`Done — made ${j.result?.fixed || 0} product(s) available${j.result?.relinked ? ` · relinked ${j.result.relinked}` : ""}${j.result?.failed ? ` · ${j.result.failed} failed` : ""}.`);
        }
      } catch { clearInterval(poll); setAllAvailBusy(false); }
    }, 3000);
  };

  const makeAllAvailable = async () => {
    if (!window.confirm("Untrack inventory + set 'continue selling' on EVERY product in your Shopify store (directly from Shopify, so nothing is missed)? This makes the whole catalog permanently sellable. Shopify only — no Printify changes. Requires the inventory permission, so make sure you've reconnected Shopify first.")) return;
    setAllAvailProgress(null);
    try {
      const { data } = await api.post("/shopify/make-all-available", {});
      toast.info("Untracking every Shopify product…");
      trackAllAvailJob(data.job_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start");
      setAllAvailBusy(false);
    }
  };

  const runAudit = async () => {
    setAuditBusy(true);
    setAudit(null);
    setAuditProgress(null);
    try {
      const { data } = await api.post("/fulfillment-audit", {});
      toast.info("Auditing fulfillment across your whole Shopify store…");
      const poll = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/jobs/${data.job_id}`);
          setAuditProgress({ done: j.completed || 0, total: j.total || 0 });
          if (["done", "error", "cancelled"].includes(j.status)) {
            clearInterval(poll);
            setAuditBusy(false);
            if (j.status === "done") { setAudit(j.result); toast.success(`Audited ${j.result?.scanned} · ${j.result?.affected_count || 0} affected · ${j.result?.repairable || 0} repairable`); }
            else toast.error("Audit hit an error — check logs");
          }
        } catch { clearInterval(poll); setAuditBusy(false); }
      }, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start audit");
      setAuditBusy(false);
    }
  };

  const runRepair = async (targets, who) => {
    const bulk = who === "bulk";
    const msg = bulk
      ? `Republish ${targets.length} affected product(s) from Printify to restore fulfillment? This re-syncs ONLY variants (fixes "manual" fulfillment) — images, gallery, title and description are left untouched.`
      : `Republish this product from Printify to restore fulfillment? Re-syncs variants only — images/gallery/copy untouched.`;
    if (!window.confirm(msg)) return;
    setRepairBusy(true);
    setRepairing(who);
    setRepairProgress(null);
    try {
      const { data } = await api.post("/fulfillment-repair", { targets });
      toast.info(bulk ? "Republishing affected products…" : "Republishing this product…");
      const poll = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/jobs/${data.job_id}`);
          setRepairProgress({ done: j.completed || 0, total: j.total || 0, log: j.log || [] });
          if (["done", "error", "cancelled"].includes(j.status)) {
            clearInterval(poll);
            setRepairBusy(false);
            setRepairing(null);
            if (j.status === "done") {
              toast.success(`Done — repaired ${j.result?.repaired || 0}${j.result?.still_manual ? ` · ${j.result.still_manual} still manual` : ""}${j.result?.failed ? ` · ${j.result.failed} failed` : ""}. Verify add-to-cart on the storefront.`);
            } else toast.error("Repair hit an error — check logs");
          }
        } catch { clearInterval(poll); setRepairBusy(false); setRepairing(null); }
      }, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start repair");
      setRepairBusy(false);
      setRepairing(null);
    }
  };

  const loadStockReport = async () => {
    setStockBusy(true);
    try {
      const { data } = await api.get("/printify/stock-report");
      setStockReport(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load stock report");
    } finally {
      setStockBusy(false);
    }
  };

  const hideSoldOut = async () => {
    const n = stockReport?.fully_sold_out || 0;
    if (!window.confirm(`Move all ${n} fully-sold-out products (mugs/tumblers with no provider stock) to Draft so they drop off the storefront? Fully reversible — use "Restore back-in-stock" when the provider restocks.`)) return;
    setHideBusy(true);
    setHideProgress(null);
    try {
      const { data } = await api.post("/printify/hide-sold-out", {});
      toast.info("Hiding sold-out products…");
      const poll = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/jobs/${data.job_id}`);
          setHideProgress({ done: j.completed || 0, total: j.total || 0 });
          if (["done", "error", "cancelled"].includes(j.status)) {
            clearInterval(poll);
            setHideBusy(false);
            if (j.status === "done") { toast.success(`Hid ${j.result?.hidden || 0} sold-out product(s).`); loadStockReport(); }
            else toast.error("Hide job hit an error — check logs");
          }
        } catch { clearInterval(poll); setHideBusy(false); }
      }, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start");
      setHideBusy(false);
    }
  };

  const restoreHidden = async () => {
    if (!window.confirm("Re-activate any hidden products that are back in stock at Printify?")) return;
    setRestoreBusy(true);
    try {
      const { data } = await api.post("/printify/restore-hidden", { force: false });
      const poll = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/jobs/${data.job_id}`);
          if (["done", "error", "cancelled"].includes(j.status)) {
            clearInterval(poll);
            setRestoreBusy(false);
            if (j.status === "done") { toast.success(`Restored ${j.result?.restored || 0} · ${j.result?.still_out_of_stock || 0} still out of stock.`); loadStockReport(); api.get("/printify/stock-digest").then(({ data }) => setStockDigest(data)).catch(() => {}); }
            else toast.error("Restore hit an error");
          }
        } catch { clearInterval(poll); setRestoreBusy(false); }
      }, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start");
      setRestoreBusy(false);
    }
  };



  const runDiagnostic = async () => {
    setDiagBusy(true);
    setDiag(null);
    setDiagProgress(null);
    try {
      const { data } = await api.post("/availability-scan", {});
      toast.info("Scanning your whole Shopify store for sold-out products…");
      const poll = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/jobs/${data.job_id}`);
          setDiagProgress({ done: j.completed || 0, total: j.total || 0 });
          if (["done", "error", "cancelled"].includes(j.status)) {
            clearInterval(poll);
            setDiagBusy(false);
            if (j.status === "done") { setDiag(j.result); toast.success(`Scanned ${j.result?.scanned} · ${j.result?.problem_count || 0} sold-out`); }
            else toast.error("Scan hit an error — check logs");
          }
        } catch { clearInterval(poll); setDiagBusy(false); }
      }, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start scan");
      setDiagBusy(false);
    }
  };

  const loadChannels = () => {
    setLoadingChannels(true);
    return api.get("/shopify/sales-channels")
      .then(({ data }) => setLiveChannels(data))
      .catch(() => setLiveChannels(null))
      .finally(() => setLoadingChannels(false));
  };

  const load = () => api.get("/channels").then(({ data }) => {
    setSettings(data.settings);
    setCounts(data.published_counts || {});
    setOauthEnabled(data.shopify_oauth_enabled);
    setShopifyConnected(data.shopify_connected);
    if (data.shopify_connected) loadChannels();
  });

  useEffect(() => {
    load();
    api.get("/branding/wrap-back").then(({ data }) => setBranding(data)).catch(() => {});
    api.get("/printify/stock-digest").then(({ data }) => setStockDigest(data)).catch(() => {});
    api.get("/settings/mockup-color").then(({ data }) => { setMockupColor(data.color); setColorOptions(data.options || []); }).catch(() => {});
    api.get("/settings/apparel-colors").then(({ data }) => { setApparelColors(data.count); setApparelColorOptions(data.options || [4, 6, 8]); }).catch(() => {});
    api.get("/settings/publish-floor").then(({ data }) => setPublishFloor(data)).catch(() => {});
    // Resume tracking an in-progress gallery-cleanup job after a page refresh.
    api.get("/jobs").then(({ data }) => {
      const list = data || [];
      const f = list.find((x) => x.kind === "remove_folded" && x.status === "running");
      if (f) { setFoldedProgress({ done: f.completed || 0, total: f.total }); trackFoldedJob(f.id, f.total); }
      const a = list.find((x) => x.kind === "fix_main_image" && x.status === "running");
      if (a) { setFixImgProgress({ done: a.completed || 0, total: a.total }); trackFixImgJob(a.id, a.total); }
      const ma = list.find((x) => x.kind === "make_all_available" && x.status === "running");
      if (ma) { setAllAvailProgress({ done: ma.completed || 0, total: ma.total }); trackAllAvailJob(ma.id); }
    }).catch(() => {});
    const q = new URLSearchParams(window.location.search).get("shopify");
    if (q === "connected") { toast.success("Shopify connected — you can now publish products live."); window.history.replaceState({}, "", "/distribution"); }
    if (q === "error") { toast.error("Shopify connection failed. Please try connecting again."); window.history.replaceState({}, "", "/distribution"); }
  }, []);

  // Save the branding preference; when applyExisting is true, also update every live wrap listing.
  const saveBranding = async (enabled, applyExisting) => {
    if (applyExisting) {
      if (!window.confirm(`${enabled ? "Add the Honorifica logo to" : "Remove the Honorifica logo from"} the back of every live wrap product now? This re-publishes those listings.`)) return;
      setApplyingBranding(true);
    }
    try {
      const { data } = await api.post("/branding/wrap-back", { enabled, apply_existing: applyExisting });
      setBranding((b) => ({ ...b, enabled: data.enabled }));
      if (data.applied) toast.success(`Updated ${data.applied.updated} live wrap product${data.applied.updated === 1 ? "" : "s"}${data.applied.errors?.length ? ` · ${data.applied.errors.length} failed` : ""}`);
      else toast.success(`Back branding ${enabled ? "enabled" : "disabled"} for new wrap products`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update branding");
      api.get("/branding/wrap-back").then(({ data }) => setBranding(data)).catch(() => {});
    } finally {
      setApplyingBranding(false);
    }
  };

  const patch = (key, upd) => setSettings((s) => ({ ...s, [key]: { ...s[key], ...upd } }));

  const refitPosters = async () => {
    if (!window.confirm("Re-fit and re-publish every live poster listing now? This corrects the artwork placement for each poster size.")) return;
    setRefitting(true);
    try {
      const { data } = await api.post("/branding/refit-posters");
      const a = data.applied || {};
      toast.success(`Re-fitted ${a.updated || 0} poster${a.updated === 1 ? "" : "s"}${a.errors?.length ? ` · ${a.errors.length} failed` : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not re-fit posters");
    } finally {
      setRefitting(false);
    }
  };

  const trimVariants = async () => {
    if (!window.confirm("Trim over-limit products down to 10 best-seller colors (60 variants) and re-publish? This fixes the Printify 'Publishing error' caused by Shopify's variant limit. Products already under the limit (mugs, phone cases) are untouched. This changes live listings and is best run once.")) return;
    setTrimming(true);
    setTrimProgress(null);
    try {
      const { data } = await api.post("/products/trim-variants/bulk", {});
      toast.info(`Trimming variants on ${data.count} product(s) & re-publishing to Shopify…`);
      const poll = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/jobs/${data.job_id}`);
          setTrimProgress({ done: j.completed || 0, total: j.total || data.count });
          if (["done", "error"].includes(j.status)) {
            clearInterval(poll);
            setTrimming(false);
            if (j.status === "done") toast.success(`Done — trimmed ${j.result?.trimmed || 0} product(s)${j.result?.failed ? ` · ${j.result.failed} failed` : ""}. Give Printify a few minutes to re-publish, then check for cleared errors.`);
            else toast.error("Trim job hit an error — check logs");
          }
        } catch { clearInterval(poll); setTrimming(false); }
      }, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start variant trim");
      setTrimming(false);
    }
  };

  const setGoogleAttrs = async () => {
    if (!window.confirm("Assign the correct Shopify category to all published products and set age group = Adult / target gender = Unisex on apparel? This fixes Google Merchant Center's 'missing age group / gender' warnings the way the native Google & YouTube channel reads them. After it finishes, resync the Google & YouTube app.")) return;
    setGattrs(true);
    setGattrsProgress(null);
    try {
      const { data } = await api.post("/products/google-attributes/bulk", {});
      toast.info(`Setting Google attributes across ${data.count} product(s)…`);
      const poll = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/jobs/${data.job_id}`);
          setGattrsProgress({ done: j.completed || 0, total: j.total || data.count });
          if (["done", "error"].includes(j.status)) {
            clearInterval(poll);
            setGattrs(false);
            if (j.status === "done") toast.success(`Done — updated ${j.result?.updated || 0} product(s)${j.result?.failed ? ` · ${j.result.failed} failed` : ""}. Now resync your Google & YouTube app.`);
            else toast.error("Google attributes job hit an error — check logs");
          }
        } catch { clearInterval(poll); setGattrs(false); }
      }, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start Google attributes update");
      setGattrs(false);
    }
  };


  const saveMockupColor = async (color) => {
    setMockupColor(color);
    try {
      await api.post("/settings/mockup-color", { color });
      toast.success(`Default color set to ${color}. New products use it automatically; run the fix below for existing ones.`);
    } catch {
      toast.error("Could not save color preference");
    }
  };

  const saveApparelColors = async (count) => {
    setApparelColors(count);
    try {
      await api.post("/settings/apparel-colors", { count });
      toast.success(`New apparel will keep ${count} colors. Going-forward only — existing products are untouched.`);
    } catch {
      toast.error("Could not save color count");
    }
  };

  const savePublishFloor = async (next) => {
    setPublishFloor(next);
    try {
      await api.post("/settings/publish-floor", next);
      toast.success(next.enabled ? `Price floor ON — retail auto-raised to at least cost × ${next.mult}` : "Price floor turned off");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save price floor");
    }
  };

  const savePremium = async (next) => {
    setPublishFloor(next);
    try {
      await api.post("/settings/publish-floor", next);
      toast.success(Number(next.premium_pct) > 0 ? `Printify Premium discount set to ${next.premium_pct}% off costs` : "Printify Premium discount turned off (using Printify's real cost)");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save Premium discount");
    }
  };

  const saveMarginFloor = async (next) => {
    setPublishFloor(next);
    try {
      await api.post("/settings/publish-floor", next);
      toast.success(next.margin_enabled ? `Margin floor ON — products under ${next.margin_floor_pct}% are blocked from publishing` : "Margin floor turned off");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save margin floor");
    }
  };


  const trackFoldedJob = (jobId, fallbackTotal) => {
    setFoldedBusy(true);
    setJobIds((s) => ({ ...s, folded: jobId }));
    const poll = setInterval(async () => {
      try {
        const { data: j } = await api.get(`/jobs/${jobId}`);
        setFoldedProgress({ done: j.completed || 0, total: j.total || fallbackTotal });
        if (["done", "error", "cancelled"].includes(j.status)) {
          clearInterval(poll);
          setFoldedBusy(false);
          setJobIds((s) => ({ ...s, folded: null }));
          if (j.status === "done") toast.success(`Done — cleaned ${j.result?.cleaned || 0} galler${(j.result?.cleaned || 0) === 1 ? "y" : "ies"}${j.result?.failed ? ` · ${j.result.failed} skipped` : ""}. Give Faire time to resync.`);
        }
      } catch { clearInterval(poll); setFoldedBusy(false); }
    }, 3000);
  };

  const shortenTitles = async () => {
    if (!window.confirm("Generate a short, clean storefront name for every product with a long title and re-sync it to Shopify? This updates the product TITLE only (no republish, no image or inventory change) — the full SEO copy stays in the description. RangeMe & Faire update on their next sync.")) return;
    setTitleProgress(null);
    try {
      const { data } = await api.post("/products/shorten-titles", { max_len: 70 });
      setTitleBusy(true);
      setJobIds((s) => ({ ...s, titles: data.job_id }));
      toast.info(`Shortening ${data.count} long title(s)…`);
      const poll = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/jobs/${data.job_id}`);
          setTitleProgress({ done: j.completed || 0, total: j.total || data.count });
          if (["done", "error", "cancelled"].includes(j.status)) {
            clearInterval(poll);
            setTitleBusy(false);
            setJobIds((s) => ({ ...s, titles: null }));
            if (j.status === "done") toast.success(`Done — shortened ${j.result?.shortened || 0} title(s). Give RangeMe/Faire time to resync.`);
          }
        } catch { clearInterval(poll); setTitleBusy(false); }
      }, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start title shortening");
      setTitleBusy(false);
    }
  };

  const reprintTm = async () => {
    setTmBusy(true);
    try {
      const { data: pv } = await api.get("/branding/reprint-tm/preview");
      if (!pv.count) { toast.success("Every logo product already prints the ™ mark."); setTmBusy(false); return; }
      const parts = Object.entries(pv.by_type).map(([t, n]) => `${n} ${t === "totebag" ? "tote" : t}${n === 1 ? "" : "s"}`).join(", ");
      if (!window.confirm(`${pv.count} product${pv.count === 1 ? "" : "s"} will be reprinted with the Honorifica™ logo (${parts}).\n\nSame logo position, size and light/dark choice — only the ™ is added. Images-only republish: titles, descriptions, prices and variants are not touched. Shopify galleries are tidied afterwards. Continue?`)) { setTmBusy(false); return; }
      const { data } = await api.post("/branding/reprint-tm");
      setTmProgress({ done: 0, total: data.count * 2 });
      toast.info(`Reprinting ${data.count} product(s) with the ™ logo…`);
      const poll = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/jobs/${data.job_id}`);
          setTmProgress({ done: j.completed || 0, total: j.total || data.count * 2 });
          if (["done", "error", "cancelled"].includes(j.status)) {
            clearInterval(poll);
            setTmBusy(false);
            if (j.status === "done") toast.success(`Reprinted ${j.result?.reprinted || 0} product(s) with the ™ logo${j.result?.failed ? ` · ${j.result.failed} failed` : ""}.`);
          }
        } catch { clearInterval(poll); setTmBusy(false); }
      }, 4000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the ™ reprint");
      setTmBusy(false);
    }
  };

  const syncPrintifyText = async () => {
    setTextBusy(true);
    try {
      const { data: pv } = await api.get("/products/sync-printify-text/preview");
      if (!pv.count) { toast.info("No products are linked to Printify."); setTextBusy(false); return; }
      const src = pv.shopify_connected ? "live Shopify title + description (Heartline's stored copy if a product isn't linked)" : "Heartline's stored copy (Shopify isn't connected)";
      if (!window.confirm(`${pv.count} product${pv.count === 1 ? "" : "s"} will be checked. Printify's title and description will be replaced with the ${src}, and Heartline will be updated to match.\n\nText only — photos, prices, variants and tags are not touched. Products already matching are skipped. Continue?`)) { setTextBusy(false); return; }
      const { data } = await api.post("/products/sync-printify-text");
      setTextProgress({ done: 0, total: data.count });
      toast.info(`Syncing text on ${data.count} product(s)…`);
      const poll = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/jobs/${data.job_id}`);
          setTextProgress({ done: j.completed || 0, total: j.total || data.count });
          if (["done", "error", "cancelled"].includes(j.status)) {
            clearInterval(poll);
            setTextBusy(false);
            if (j.status === "done") toast.success(`Done — ${j.result?.updated || 0} updated in Printify, ${j.result?.unchanged || 0} already matched${j.result?.failed ? ` · ${j.result.failed} failed` : ""}.`);
          }
        } catch { clearInterval(poll); setTextBusy(false); }
      }, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the text sync");
      setTextBusy(false);
    }
  };

  const sanitizeClaims = async () => {
    if (!window.confirm("Scan every product and remove false-advertising wording like \"hand drawn\", \"handmade\" or \"hand painted\" from the description — both in Heartline and on the live Shopify listing? These are digitally designed POD items, so the wording is replaced with \"original\" / \"specially designed\". Shopify description text only: no republish, no image or inventory change.")) return;
    setClaimsProgress(null);
    try {
      const { data } = await api.post("/products/sanitize-descriptions", {});
      setClaimsBusy(true);
      setJobIds((s) => ({ ...s, claims: data.job_id }));
      toast.info(`Scanning ${data.count} product(s) for false-craft wording…`);
      const poll = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/jobs/${data.job_id}`);
          setClaimsProgress({ done: j.completed || 0, total: j.total || data.count });
          if (["done", "error", "cancelled"].includes(j.status)) {
            clearInterval(poll);
            setClaimsBusy(false);
            setJobIds((s) => ({ ...s, claims: null }));
            if (j.status === "done") toast.success(`Done — cleaned ${j.result?.cleaned_shopify || 0} live listing(s) and ${j.result?.cleaned_stored || 0} stored description(s). Give Faire/RangeMe time to resync.`);
          }
        } catch { clearInterval(poll); setClaimsBusy(false); }
      }, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No false-craft wording found");
      setClaimsBusy(false);
    }
  };

  const stampWalmartId = async () => {
    if (!window.confirm("Stamp the Walmart GTIN-exemption identifier \"CUSTOM\" onto every product linked to Shopify? This writes a dedicated metafield (custom.walmart_product_id = CUSTOM) that you map ONCE in Marketplace Connect → Attribute Mode → Product Identifiers → Product ID. It does NOT touch the barcode field, so your Google feed stays clean. Metadata only — no inventory/image/price change. New products get it automatically on publish.")) return;
    setWalmartProgress(null);
    try {
      const { data } = await api.post("/products/stamp-walmart-id", {});
      setWalmartBusy(true);
      toast.info(`Stamping "CUSTOM" on ${data.count} product(s)…`);
      const poll = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/jobs/${data.job_id}`);
          setWalmartProgress({ done: j.completed || 0, total: j.total || data.count });
          if (["done", "error", "cancelled"].includes(j.status)) {
            clearInterval(poll);
            setWalmartBusy(false);
            if (j.status === "done") toast.success(`Done — stamped ${j.result?.stamped || 0} product(s). Now map it once in Marketplace Connect → Attribute Mode → Product ID.`);
          }
        } catch { clearInterval(poll); setWalmartBusy(false); }
      }, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not stamp Walmart identifier");
      setWalmartBusy(false);
    }
  };

  const stampApparelAttrs = async () => {
    if (!window.confirm("Stamp Walmart apparel attributes (age group, fabric, neck/sleeve style, and a Walmart-valid color category per variant) onto all your tees, hoodies and sweatshirts? These are written as Shopify metafields (namespace 'walmart') that you map ONCE in Marketplace Connect → Category Specific Attributes. Metadata only — no inventory/image/price change. New apparel gets them automatically on publish.")) return;
    setApparelProgress(null);
    try {
      const { data } = await api.post("/products/stamp-apparel-attrs", {});
      setApparelBusy(true);
      toast.info(`Stamping apparel attributes on ${data.count} product(s)…`);
      const poll = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/jobs/${data.job_id}`);
          setApparelProgress({ done: j.completed || 0, total: j.total || data.count });
          if (["done", "error", "cancelled"].includes(j.status)) {
            clearInterval(poll);
            setApparelBusy(false);
            if (j.status === "done") {
              const unm = j.result?.unmapped_colors || [];
              toast.success(`Done — stamped ${j.result?.stamped || 0} apparel product(s). Now map the 'walmart' attributes once in Marketplace Connect.`);
              if (unm.length) toast.warning(`${unm.length} color(s) couldn't be auto-matched (set to Multicolor) — review: ${unm.slice(0, 12).join(", ")}`, { duration: 12000 });
            }
          }
        } catch { clearInterval(poll); setApparelBusy(false); }
      }, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not stamp apparel attributes");
      setApparelBusy(false);
    }
  };


  const reattachSingle = async () => {
    if (!window.confirm("Attach the front photo to EVERY model/size variant of phone cases, mugs, posters, stickers and other single-design products, so no variant shows without a photo? Printify only mocks up a few models, so every model will show the same front mockup. Shopify photos only — nothing is republished, Printify is not touched.")) return;
    setSingleBusy(true);
    try {
      const { data } = await api.post("/products/reattach-single-photos", {});
      setSingleProgress({ done: 0, total: data.count });
      toast.info(`Re-attaching photos on ${data.count} product(s)…`);
      const poll = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/jobs/${data.job_id}`);
          setSingleProgress({ done: j.completed || 0, total: j.total || data.count });
          if (["done", "error", "cancelled"].includes(j.status)) {
            clearInterval(poll);
            setSingleBusy(false);
            if (j.status === "done") toast.success(`Done — photos attached on ${j.result?.cleaned || 0} product(s)${j.result?.failed ? ` · ${j.result.failed} skipped` : ""}.`);
          }
        } catch { clearInterval(poll); setSingleBusy(false); }
      }, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start photo re-attach");
      setSingleBusy(false);
    }
  };

  const removeFolded = async (force = false) => {
    const msg = force
      ? "Re-apply the gallery rebuild to EVERY live product — including ones already cleaned? This re-attaches each color's photo to its color variants (fixes the 'only white shows' issue) and keeps galleries front-only. Shopify images only — does NOT touch Printify, fully recoverable. Faire updates on its next sync."
      : "Rebuild every live product's image gallery with only the clean front/back photos, removing the folded shots (so they no longer show on Shopify or Faire)? This changes Shopify images only — it does NOT touch Printify, and is fully recoverable. Already-cleaned products are skipped, so this safely resumes if a previous run was interrupted. Faire updates on its next sync.";
    if (!window.confirm(msg)) return;
    setFoldedProgress(null);
    try {
      const { data } = await api.post(`/products/remove-folded-images${force ? "?force=true" : ""}`, {});
      toast.info(`${force ? "Re-applying galleries" : "Cleaning galleries"} on ${data.count} product(s)…`);
      trackFoldedJob(data.job_id, data.count);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start gallery cleanup");
      setFoldedBusy(false);
    }
  };

  const trackFixImgJob = (jobId, fallbackTotal) => {
    setFixImg(true);
    setJobIds((s) => ({ ...s, fixImg: jobId }));
    const poll = setInterval(async () => {
      try {
        const { data: j } = await api.get(`/jobs/${jobId}`);
        setFixImgProgress({ done: j.completed || 0, total: j.total || fallbackTotal });
        if (["done", "error", "cancelled"].includes(j.status)) {
          clearInterval(poll);
          setFixImg(false);
          setJobIds((s) => ({ ...s, fixImg: null }));
          if (j.status === "done") toast.success(`Done — fixed ${j.result?.fixed || 0} product(s)${j.result?.failed ? ` · ${j.result.failed} skipped` : ""}. Give Faire/Google time to resync.`);
        }
      } catch { clearInterval(poll); setFixImg(false); }
    }, 3000);
  };

  const fixMainImage = async () => {
    if (!window.confirm(`Restore all live products to sellable (fix "sold out") and set the ${mockupColor} FRONT as the main image? This updates Shopify only (continue-selling + main image) and does NOT re-publish through Printify. Changes flow to Faire/Google on their next sync.`)) return;
    setFixImgProgress(null);
    try {
      const { data } = await api.post("/products/fix-main-image", {});
      toast.info(`Fixing main image on ${data.count} live product(s)…`);
      trackFixImgJob(data.job_id, data.count);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start main-image fix");
      setFixImg(false);
    }
  };


  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/channels/settings", { channels: settings });
      setSettings(data);
      toast.success("Distribution settings saved");
    } finally { setSaving(false); }
  };

  const connectShopify = async () => {
    const shop = settings.shopify?.shop_domain?.trim();
    if (!shop) return toast.error("Enter your store domain first (e.g. your-shop.myshopify.com)");
    setConnecting(true);
    try {
      const { data } = await api.get(`/shopify/oauth/start?shop=${encodeURIComponent(shop)}&redirect_base=${encodeURIComponent(window.location.origin)}`);
      // Open in a new top-level tab — Shopify's OAuth page refuses to load inside an iframe.
      const win = window.open(data.authorize_url, "_blank", "noopener,noreferrer");
      if (!win) {
        try { window.top.location.href = data.authorize_url; }
        catch (_) { window.location.href = data.authorize_url; }
      } else {
        toast.info("Complete the Shopify authorization in the new tab, then return here.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start Shopify connection");
    } finally {
      setConnecting(false);
    }
  };

  const disconnectShopify = async () => {
    await api.post("/shopify/disconnect");
    toast.success("Shopify disconnected");
    load();
  };

  if (!settings) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;

  const hub = SALES_CHANNELS.find((c) => c.role === "hub");
  const spokes = SALES_CHANNELS.filter((c) => c.role === "spoke");

  return (
    <div className="max-w-4xl mx-auto" data-testid="distribution-page">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="overline text-blue-600">Distribution</p>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Sales channels</h1>
          <p className="text-sm text-zinc-500 mt-1">One generated product → published to Shopify → fanned out to every connected marketplace. <span className="text-zinc-400">Shopify is live via OAuth; other channels are handled inside Shopify.</span></p>
        </div>
        <a href="/heartline-workflow.html" target="_blank" rel="noreferrer" data-testid="workflow-graphic-link" className="h-9 px-3 rounded-md border border-zinc-200 bg-white hover:bg-zinc-50 text-sm font-medium flex items-center gap-1.5 transition-colors shrink-0">
          <Workflow className="w-4 h-4 text-blue-600" /> View workflow diagram
        </a>
      </div>

      {stockDigest && stockDigest.count > 0 && (
        <div className="flex items-start gap-3 mb-4 rounded-lg bg-emerald-50 border border-emerald-300 p-4" data-testid="restock-digest-banner">
          <Check className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-emerald-900" data-testid="restock-digest-count">
              {stockDigest.count} hidden product{stockDigest.count === 1 ? "" : "s"} {stockDigest.count === 1 ? "is" : "are"} back in stock at Printify
            </p>
            <p className="text-xs text-emerald-700 mt-0.5">
              Click Restore to make {stockDigest.count === 1 ? "it" : "them"} live on the storefront again.
              {stockDigest.last_run ? <span className="text-emerald-600/70"> · checked {new Date(stockDigest.last_run).toLocaleDateString()}</span> : null}
            </p>
          </div>
          <button
            onClick={restoreHidden}
            disabled={!shopifyConnected || restoreBusy}
            data-testid="restock-digest-restore-btn"
            className="h-9 px-4 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-semibold flex items-center gap-1.5 transition-colors shrink-0"
          >
            {restoreBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            {restoreBusy ? "Restoring…" : "Restore now"}
          </button>
        </div>
      )}


      <div className="bg-white border-2 border-zinc-900 rounded-lg p-5 mb-4" data-testid="channel-shopify">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2"><Store className="w-5 h-5" /><h2 className="font-display text-lg font-bold">{hub.label}</h2><span className="text-[9px] px-1.5 py-0.5 rounded bg-zinc-900 text-white">HUB</span>
              {shopifyConnected && <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">LIVE</span>}</div>
            <p className="text-sm text-zinc-500 mt-1">{hub.desc}</p>
            {shopifyConnected && settings.shopify?.shop_name && <p className="text-xs text-emerald-700 mt-1">Connected to {settings.shopify.shop_name}</p>}
          </div>
          {shopifyConnected ? (
            <button onClick={disconnectShopify} data-testid="disconnect-shopify" className="h-9 px-3 text-sm rounded-md font-medium flex items-center gap-1.5 border bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100">
              <Check className="w-4 h-4" /> Connected
            </button>
          ) : (
            <button onClick={connectShopify} disabled={connecting || !oauthEnabled} data-testid="connect-shopify" className="h-9 px-3 text-sm rounded-md font-medium flex items-center gap-1.5 border bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-50">
              {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />} Connect Shopify
            </button>
          )}
        </div>
        <div className="mt-3">
          <span className="text-[10px] text-zinc-400">Shop domain</span>
          <Input value={settings.shopify?.shop_domain || ""} onChange={(e) => patch("shopify", { shop_domain: e.target.value })} disabled={shopifyConnected} placeholder="your-shop.myshopify.com" className="h-9 text-sm mt-1 max-w-xs" data-testid="shopify-domain" />
          {!shopifyConnected && <p className="text-[11px] text-zinc-400 mt-1.5">Enter your store domain, then click Connect Shopify to authorize via OAuth.</p>}
        </div>
      </div>

      {shopifyConnected && (
        <div className="bg-white border border-zinc-200 rounded-lg p-4 mb-4" data-testid="live-channels-card">
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-semibold text-sm flex items-center gap-1.5"><Store className="w-4 h-4 text-blue-600" /> Connected sales channels <span className="text-[10px] font-normal text-zinc-400">· live from Shopify</span></h3>
            <button onClick={loadChannels} disabled={loadingChannels} data-testid="refresh-channels-btn" className="h-7 px-2 rounded-md border border-zinc-200 hover:bg-zinc-50 text-xs flex items-center gap-1 disabled:opacity-50">
              <RefreshCw className={`w-3.5 h-3.5 ${loadingChannels ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
          <div className="mt-3">
            {loadingChannels && !liveChannels ? (
              <p className="text-xs text-zinc-400">Checking your Shopify sales channels…</p>
            ) : liveChannels?.available ? (
              liveChannels.channels.length ? (
                <div className="flex flex-wrap gap-2" data-testid="live-channels-list">
                  {liveChannels.channels.map((ch) => (
                    <span key={ch.id} data-testid={`live-channel-${ch.name}`} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-medium">
                      <Check className="w-3.5 h-3.5" /> {ch.name}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-zinc-500">No sales channels found yet. Add TikTok, Etsy, or Faire from your Shopify admin → Settings → Apps and sales channels.</p>
              )
            ) : liveChannels?.reason === "scope" ? (
              <p className="text-xs text-amber-600">Reconnect Shopify to grant read access, then this will list your live channels (TikTok, Etsy, Faire, Online Store) automatically.</p>
            ) : (
              <p className="text-xs text-zinc-400">Couldn't read channels right now. Try Refresh, or check the Shopify connection.</p>
            )}
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-3 mb-3">
        {spokes.map((c) => (
          <div key={c.key} className="bg-white border border-zinc-200 rounded-lg p-4" data-testid={`channel-${c.key}`}>
            <h3 className="font-semibold text-sm flex items-center gap-1.5">{c.label} {c.wholesale && <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-700">Wholesale</span>}</h3>
            <p className="text-xs text-zinc-500 mt-1 min-h-[48px]">{c.desc}</p>
            <div className="text-[10px] text-zinc-400 mt-1" data-testid={`count-${c.key}`}>{counts[c.key] || 0} live listings</div>
            {c.wholesale && (
              <div className="mt-2">
                <span className="text-[10px] text-zinc-400">Faire order minimum</span>
                <div className="relative max-w-[120px]">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400 text-xs">$</span>
                  <Input type="number" min="0" value={settings.faire?.order_minimum ?? 100} onChange={(e) => patch("faire", { order_minimum: Number(e.target.value) })} className="h-8 text-sm pl-5 mt-1" data-testid="faire-min" />
                </div>
              </div>
            )}
            <div className="mt-3"><span data-testid={`managed-${c.key}`} className="inline-flex items-center gap-1.5 h-8 px-2.5 text-xs rounded-md font-medium border bg-zinc-50 border-zinc-200 text-zinc-500"><Store className="w-3.5 h-3.5" /> Managed in Shopify</span></div>
          </div>
        ))}
      </div>

      {branding && (
        <div className="bg-white border border-zinc-200 rounded-lg p-4 mb-3" data-testid="wrap-branding-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="max-w-lg">
              <h3 className="font-semibold text-sm flex items-center gap-1.5"><Heart className="w-4 h-4 text-[#C05A45]" /> Wrap-product back branding</h3>
              <p className="text-xs text-zinc-500 mt-1">Print the Honorifica logo on the back of wrap products ({(branding.wrap_types || []).map((t) => t === "totebag" ? "tote bags" : t).join(", ") || "tote bags"}). Applies to newly published products automatically.</p>
            </div>
            <label className="flex items-center gap-2 text-xs font-medium shrink-0">
              <Switch checked={!!branding.enabled} onCheckedChange={(v) => saveBranding(v, false)} data-testid="wrap-branding-toggle" />
              {branding.enabled ? "On" : "Off"}
            </label>
          </div>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <button
              onClick={() => saveBranding(branding.enabled, true)}
              disabled={applyingBranding || !branding.printify_configured}
              data-testid="apply-wrap-branding-btn"
              className="h-9 px-3 rounded-md bg-zinc-900 hover:bg-zinc-800 disabled:opacity-50 text-white text-xs font-medium flex items-center gap-1.5 transition-colors"
            >
              {applyingBranding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              {applyingBranding ? "Updating live listings…" : `Apply "${branding.enabled ? "logo on" : "logo off"}" to all live wrap products`}
            </button>
            <span className="text-[11px] text-zinc-400">Re-publishes existing wrap listings to match the setting above.</span>
          </div>
          <div className="mt-3 pt-3 border-t border-zinc-100 flex items-center gap-3 flex-wrap" data-testid="reprint-tm-row">
            <button
              onClick={reprintTm}
              disabled={tmBusy || !branding.printify_configured}
              data-testid="reprint-tm-btn"
              className="h-9 px-3 rounded-md bg-[#C05A45] hover:bg-[#a84c39] disabled:opacity-50 text-white text-xs font-medium flex items-center gap-1.5 transition-colors"
            >
              {tmBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              {tmBusy ? "Reprinting with ™ logo…" : "Reprint mugs, posters & totes with ™ logo"}
            </button>
            {tmProgress && <span className="text-[11px] text-zinc-500" data-testid="reprint-tm-progress">{tmProgress.done}/{tmProgress.total} steps</span>}
            <span className="text-[11px] text-zinc-400">Only products that already carry the logo. Shows a count first.</span>
          </div>
        </div>
      )}

      {branding && (
        <div className="bg-white border border-zinc-200 rounded-lg p-4 mb-3" data-testid="poster-refit-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="max-w-lg">
              <h3 className="font-semibold text-sm flex items-center gap-1.5"><RefreshCw className="w-4 h-4 text-[#C05A45]" /> Re-fit existing posters</h3>
              <p className="text-xs text-zinc-500 mt-1">New posters now scale the artwork to fit each poster size (portrait, landscape and square). Posters published earlier keep their old placement — run this once to re-fit and re-publish every live poster listing.</p>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <button
              onClick={refitPosters}
              disabled={refitting || !branding.printify_configured}
              data-testid="refit-posters-btn"
              className="h-9 px-3 rounded-md bg-zinc-900 hover:bg-zinc-800 disabled:opacity-50 text-white text-xs font-medium flex items-center gap-1.5 transition-colors"
            >
              {refitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              {refitting ? "Re-fitting posters…" : "Re-fit all live posters"}
            </button>
            <span className="text-[11px] text-zinc-400">Re-publishes existing poster listings so Shopify pulls corrected mockups.</span>
          </div>
        </div>
      )}

      {branding && (
        <div className="bg-white border border-zinc-200 rounded-lg p-4 mb-3" data-testid="variant-trim-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="max-w-lg">
              <h3 className="font-semibold text-sm flex items-center gap-1.5"><Workflow className="w-4 h-4 text-[#C05A45]" /> Fix Printify "Publishing error" (variant limit)</h3>
              <p className="text-xs text-zinc-500 mt-1">Shopify caps products at 100 variants and limits total variants across the store's shipping profile — when apparel is published with all 18 colors × 6 sizes (108), publishing fails for every product. This trims over-limit products to <strong>10 best-seller colors (60 variants)</strong> and re-publishes. Mugs and phone cases (already under the limit) are left untouched.</p>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <button
              onClick={trimVariants}
              disabled={trimming || !branding.printify_configured}
              data-testid="trim-variants-btn"
              className="h-9 px-3 rounded-md bg-[#C05A45] hover:bg-[#a94a38] disabled:opacity-50 text-white text-xs font-medium flex items-center gap-1.5 transition-colors"
            >
              {trimming ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Workflow className="w-3.5 h-3.5" />}
              {trimming ? `Trimming… ${trimProgress ? `${trimProgress.done}/${trimProgress.total}` : ""}` : "Trim variants on all products"}
            </button>
            <span className="text-[11px] text-zinc-400">Keeps: Black, White, Navy, Sport Grey, Red, Royal, Maroon, Forest, Sand, Light Blue, Purple, Charcoal.</span>
          </div>
        </div>
      )}

      {branding && (
        <div className="bg-white border border-zinc-200 rounded-lg p-4 mb-3" data-testid="google-attrs-card">
          <div className="max-w-lg">
            <h3 className="font-semibold text-sm flex items-center gap-1.5"><Tag className="w-4 h-4 text-[#C05A45]" /> Fix Google Merchant "missing age group / gender"</h3>
            <p className="text-xs text-zinc-500 mt-1">Google's native feed reads the <strong>Shopify category</strong> attributes — not custom metafields. This assigns each product's correct <strong>standard category</strong> and sets <strong>age group = Adult</strong> / <strong>target gender = Unisex</strong> on apparel (shirts, hoodies, sweatshirts). Non-apparel (mugs, posters) just get categorized correctly so Google stops requiring age/gender for them. After it runs, resync the Google &amp; YouTube app.</p>
          </div>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <button
              onClick={setGoogleAttrs}
              disabled={gattrs || !shopifyConnected}
              data-testid="google-attrs-btn"
              className="h-9 px-3 rounded-md bg-[#C05A45] hover:bg-[#a94a38] disabled:opacity-50 text-white text-xs font-medium flex items-center gap-1.5 transition-colors"
            >
              {gattrs ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Tag className="w-3.5 h-3.5" />}
              {gattrs ? `Updating… ${gattrsProgress ? `${gattrsProgress.done}/${gattrsProgress.total}` : ""}` : "Set Google attributes on all products"}
            </button>
            <span className="text-[11px] text-zinc-400">Shipping errors are separate — set those once in Merchant Center → Shipping and returns.</span>
          </div>
        </div>
      )}

      {branding?.printify_configured && (
        <div className="bg-white border-2 border-zinc-900 rounded-lg p-4 mb-3" data-testid="stock-report-card">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h3 className="font-semibold text-sm flex items-center gap-1.5"><Tag className="w-4 h-4 text-zinc-900" /> Printify provider stock — what's unbuyable & why</h3>
            <button
              onClick={loadStockReport}
              disabled={stockBusy}
              data-testid="load-stock-report-btn"
              className="h-8 px-3 rounded-md border border-zinc-300 text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 text-xs font-medium flex items-center gap-1.5 transition-colors"
            >
              {stockBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              {stockBusy ? "Loading…" : stockReport ? "Refresh" : "Load stock report"}
            </button>
          </div>
          <p className="text-xs text-zinc-500 mt-1">
            Live from Printify. A product shows "Sold out" on the storefront when its print provider is out of stock — not a Shopify setting. Fully-sold-out products can't be bought at all and should be hidden until the provider restocks.
          </p>

          {stockReport && (
            <div className="mt-3">
              <p className="text-xs mb-2" data-testid="stock-report-summary">
                <strong>{stockReport.total}</strong> products · <strong className="text-emerald-600">{stockReport.ok}</strong> fully sellable ·
                {" "}<strong className={stockReport.partial ? "text-amber-600" : "text-zinc-500"}>{stockReport.partial}</strong> partial (some variants out) ·
                {" "}<strong className={stockReport.fully_sold_out ? "text-red-600" : "text-emerald-600"}>{stockReport.fully_sold_out}</strong> fully sold out
                {stockReport.hidden_count ? <span className="text-zinc-500"> · {stockReport.hidden_count} currently hidden</span> : null}
              </p>
              {Object.keys(stockReport.affected_by_type || {}).length > 0 && (
                <p className="text-[11px] text-zinc-500 mb-2">
                  Affected types: {Object.entries(stockReport.affected_by_type).map(([t, n]) => `${t} (${n})`).join(" · ")}
                </p>
              )}

              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={hideSoldOut}
                  disabled={!shopifyConnected || hideBusy || !stockReport.fully_sold_out}
                  data-testid="hide-sold-out-btn"
                  className="h-9 px-3 rounded-md bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs font-medium flex items-center gap-1.5 transition-colors"
                >
                  {hideBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                  {hideBusy ? `Hiding… ${hideProgress ? `${hideProgress.done}/${hideProgress.total}` : ""}` : `Hide ${stockReport.fully_sold_out} sold-out products (Draft)`}
                </button>
                <button
                  onClick={restoreHidden}
                  disabled={!shopifyConnected || restoreBusy || !stockReport.hidden_count}
                  data-testid="restore-hidden-btn"
                  className="h-9 px-3 rounded-md border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 text-xs font-medium flex items-center gap-1.5 transition-colors"
                >
                  {restoreBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                  {restoreBusy ? "Restoring…" : `Restore back-in-stock${stockReport.hidden_count ? ` (${stockReport.hidden_count} hidden)` : ""}`}
                </button>
                {!shopifyConnected && <span className="text-[11px] text-amber-600">Connect Shopify to hide/restore.</span>}
                <button
                  onClick={() => setShowStockTable((v) => !v)}
                  data-testid="toggle-stock-table-btn"
                  className="h-9 px-3 rounded-md border border-zinc-200 text-zinc-600 hover:bg-zinc-50 text-xs font-medium transition-colors"
                >
                  {showStockTable ? "Hide list" : `Show ${stockReport.fully_sold_out + stockReport.partial} affected`}
                </button>
              </div>

              {showStockTable && (
                <div className="mt-3 rounded-md border border-zinc-200 bg-zinc-50 p-3 overflow-x-auto max-h-96 overflow-y-auto" data-testid="stock-report-table">
                  <table className="text-[11px] w-full">
                    <thead className="text-zinc-500 sticky top-0 bg-zinc-50">
                      <tr className="text-left">
                        <th className="pr-3 pb-1">Product</th><th className="pr-3 pb-1">Type</th>
                        <th className="pr-3 pb-1">Stock</th><th className="pr-3 pb-1">Out-of-stock variants</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(stockReport.products || []).filter((r) => r.state !== "ok").map((r, i) => (
                        <tr key={i} className="border-t border-zinc-200" data-testid={`stock-row-${i}`}>
                          <td className="pr-3 py-1 max-w-[240px] truncate">{r.title}</td>
                          <td className="pr-3 py-1 text-zinc-500">{r.type}</td>
                          <td className={`pr-3 py-1 font-medium ${r.state === "fully_sold_out" ? "text-red-600" : "text-amber-600"}`}>
                            {r.state === "fully_sold_out" ? "SOLD OUT" : `${r.in_stock}/${r.enabled}`}
                          </td>
                          <td className="pr-3 py-1 text-zinc-500 max-w-[220px] truncate">{(r.oos_variants || []).join(", ") || "all"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}


      {shopifyConnected && (
        <div className="bg-white border-2 border-[#C05A45] rounded-lg p-4 mb-3" data-testid="fulfillment-repair-card">
          <h3 className="font-semibold text-sm flex items-center gap-1.5"><Wrench className="w-4 h-4 text-[#C05A45]" /> Sold out but IN STOCK at Printify? — fulfillment repair</h3>
          <p className="text-xs text-zinc-500 mt-1 mb-3">
            Use this only for products the stock report shows are <strong>in stock at Printify</strong> yet still say "Sold out" on the storefront
            (e.g. some t-shirts). That's a Shopify fulfillment-state issue — a variant flipped to <strong>fulfillment&nbsp;service = manual</strong>.
            The fix is to <strong>republish from Printify</strong> (variants only) to restore Printify fulfillment ownership — images, gallery and copy
            are left untouched. Mugs/tumblers that are genuinely out of stock at the provider won't be fixed here — hide those above instead.
          </p>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={runAudit}
              disabled={auditBusy || repairBusy}
              data-testid="fulfillment-audit-btn"
              className="h-9 px-3 rounded-md border border-zinc-300 text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 text-xs font-medium flex items-center gap-1.5 transition-colors"
            >
              {auditBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Info className="w-3.5 h-3.5" />}
              {auditBusy ? `Auditing… ${auditProgress ? `${auditProgress.done}/${auditProgress.total}` : ""}` : "Audit fulfillment (read-only)"}
            </button>
            {audit && audit.repairable > 0 && (
              <button
                onClick={() => runRepair((audit.affected || []).filter((a) => a.printify_id).map((a) => ({ printify_id: a.printify_id, shopify_id: a.shopify_id, title: a.title })), "bulk")}
                disabled={repairBusy}
                data-testid="fulfillment-repair-all-btn"
                className="h-9 px-3 rounded-md bg-[#C05A45] hover:bg-[#a94a38] disabled:opacity-50 text-white text-xs font-medium flex items-center gap-1.5 transition-colors"
              >
                {repairBusy && repairing === "bulk" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                {repairBusy && repairing === "bulk" ? `Repairing… ${repairProgress ? `${repairProgress.done}/${repairProgress.total}` : ""}` : `Repair all ${audit.repairable} affected (bulk)`}
              </button>
            )}
          </div>

          {audit && (
            <div className="mt-3 rounded-md border border-zinc-200 bg-zinc-50 p-3 overflow-x-auto" data-testid="fulfillment-audit-results">
              <p className="text-xs mb-2" data-testid="fulfillment-audit-summary">
                Audited <strong>{audit.scanned}</strong> · <strong className="text-emerald-600">{audit.healthy}</strong> healthy ·
                {" "}<strong className={audit.affected_count ? "text-red-600" : "text-emerald-600"}>{audit.affected_count}</strong> affected ·
                {" "}<strong className="text-[#C05A45]">{audit.repairable}</strong> repairable
                {audit.no_printify_link ? <span className="text-amber-600"> · {audit.no_printify_link} without a Printify link</span> : null}
              </p>
              {audit.affected_count === 0 ? (
                <p className="text-[11px] text-emerald-700">No products have "manual" fulfillment — every live product is fulfilled by Printify. If the storefront still shows sold out, it's a theme/cache issue, not fulfillment.</p>
              ) : (
                <table className="text-[11px] w-full">
                  <thead className="text-zinc-500">
                    <tr className="text-left">
                      <th className="pr-3 pb-1">Affected product</th><th className="pr-3 pb-1">Manual variants</th>
                      <th className="pr-3 pb-1">Printify link</th><th className="pb-1"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(audit.affected || []).map((r, i) => (
                      <tr key={i} className="border-t border-zinc-200" data-testid={`audit-row-${i}`}>
                        <td className="pr-3 py-1 max-w-[240px] truncate">{r.title || "—"}{r.reason ? <span className="text-amber-600"> · {r.reason}</span> : null}</td>
                        <td className="pr-3 py-1 text-red-600 font-medium">{r.manual_variants ?? 0}/{r.total_variants ?? 0}</td>
                        <td className="pr-3 py-1">{r.printify_id ? <span className="text-emerald-600">linked</span> : <span className="text-amber-600">none</span>}</td>
                        <td className="py-1">
                          {r.printify_id && (
                            <button
                              onClick={() => runRepair([{ printify_id: r.printify_id, shopify_id: r.shopify_id, title: r.title }], r.shopify_id)}
                              disabled={repairBusy}
                              data-testid={`repair-pilot-${i}`}
                              className="h-6 px-2 rounded border border-[#C05A45] text-[#C05A45] hover:bg-[#fdf3f0] disabled:opacity-50 text-[10px] font-medium"
                            >
                              {repairBusy && repairing === r.shopify_id ? "…" : "Republish (pilot)"}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {repairProgress && (repairProgress.log || []).length > 0 && (
                <div className="mt-2 pt-2 border-t border-zinc-200 max-h-32 overflow-y-auto" data-testid="repair-log">
                  {(repairProgress.log || []).slice(-8).map((l, i) => (
                    <p key={i} className="text-[10px] text-zinc-600 font-mono">{l}</p>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-zinc-500 mt-2">
                Recommended flow: run one <strong>Republish (pilot)</strong>, then verify you can add that product to cart on the storefront, then use <strong>Repair all</strong>.
              </p>
            </div>
          )}
        </div>
      )}

      {shopifyConnected && (
        <div className="flex items-start gap-2 mb-3 rounded-lg bg-amber-50 border border-amber-200 p-3" data-testid="untrack-deprecated-note">
          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-[11px] text-amber-800">
            <strong>Note:</strong> the older "Restore availability" / "Make ALL available" / single-URL fix tools below used to <em>untrack inventory</em> — we've discovered that action is what flipped variants to "manual" fulfillment and caused the sold-out issue, so it's now <strong>disabled</strong> (those buttons no longer change inventory). Use <strong>Fulfillment repair</strong> above instead. The "Set main image color" and "Remove folded images" tools are still safe.
          </p>
        </div>
      )}


      <div className="bg-white border border-zinc-200 rounded-lg p-4 mb-3" data-testid="apparel-colors-card">
          <div className="max-w-lg">
            <h3 className="font-semibold text-sm flex items-center gap-1.5"><ImageIcon className="w-4 h-4 text-[#C05A45]" /> Apparel color count (new products)</h3>
            <p className="text-xs text-zinc-500 mt-1">How many colors each <strong>new</strong> t-shirt / hoodie / sweatshirt keeps. Colors are chosen from best-sellers and filtered so the design never disappears on a clashing color. Fewer colors = a cleaner storefront and a far more usable RangeMe catalog (one row per color × size). <strong>Going-forward only</strong> — existing products are untouched.</p>
          </div>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <label className="text-xs text-zinc-600 flex items-center gap-1.5">
              Colors per apparel product
              <select
                value={apparelColors}
                onChange={(e) => saveApparelColors(Number(e.target.value))}
                data-testid="apparel-colors-select"
                className="h-9 px-2 rounded-md border border-zinc-200 bg-white text-xs focus:outline-none focus:ring-1 focus:ring-[#C05A45]"
              >
                {apparelColorOptions.map((c) => (
                  <option key={c} value={c}>{c} colors</option>
                ))}
              </select>
            </label>
            <span className="text-[11px] text-zinc-400">e.g. {apparelColors} colors × ~5 sizes = {apparelColors * 5} variants (vs 100+ before)</span>
          </div>
        </div>

      <div className="bg-white border border-zinc-200 rounded-lg p-4 mb-3" data-testid="publish-floor-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="max-w-lg">
            <h3 className="font-semibold text-sm flex items-center gap-1.5"><DollarSign className="w-4 h-4 text-[#C05A45]" /> Cost-based price floor (at publish)</h3>
            <p className="text-xs text-zinc-500 mt-1">When ON, every product's retail price is automatically raised to at least <strong>real Printify cost × the multiple below</strong> at publish and rebaseline time — so a build or a repricing run can <strong>never</strong> push a product below production cost. Wholesale is also kept at or above cost.</p>
          </div>
          <Switch checked={!!publishFloor.enabled} onCheckedChange={(v) => savePublishFloor({ ...publishFloor, enabled: v })} data-testid="publish-floor-toggle" />
        </div>
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <label className="text-xs text-zinc-600 flex items-center gap-1.5">
            Minimum retail = cost ×
            <Input
              type="number" step="0.1" min="1.2" max="5"
              value={publishFloor.mult}
              onChange={(e) => setPublishFloor({ ...publishFloor, mult: Number(e.target.value) })}
              onBlur={() => savePublishFloor({ ...publishFloor, mult: publishFloor.mult })}
              disabled={!publishFloor.enabled}
              data-testid="publish-floor-mult"
              className="h-9 w-20 text-sm"
            />
          </label>
          <span className="text-[11px] text-zinc-400">e.g. a $6.44 mug → floor ${(6.44 * (publishFloor.mult || 2.2)).toFixed(2)} retail</span>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg p-4 mb-3" data-testid="margin-floor-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="max-w-lg">
            <h3 className="font-semibold text-sm flex items-center gap-1.5"><DollarSign className="w-4 h-4 text-[#C05A45]" /> Retail-margin publish gate</h3>
            <p className="text-xs text-zinc-500 mt-1">When ON, any product whose <strong>shipping-absorbed retail margin</strong> is below the floor below is <strong>blocked from publishing to Printify</strong> (a gatekeeper, not a price bump). Products with no cost/price loaded are blocked too. You can still override per-publish with “Publish anyway”. This keeps your 45% target aspirational while only hard-stopping genuinely thin items.</p>
          </div>
          <Switch checked={!!publishFloor.margin_enabled} onCheckedChange={(v) => saveMarginFloor({ ...publishFloor, margin_enabled: v })} data-testid="margin-floor-toggle" />
        </div>
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <label className="text-xs text-zinc-600 flex items-center gap-1.5">
            Block below margin
            <Input
              type="number" step="1" min="0" max="90"
              value={publishFloor.margin_floor_pct}
              onChange={(e) => setPublishFloor({ ...publishFloor, margin_floor_pct: Number(e.target.value) })}
              onBlur={() => saveMarginFloor({ ...publishFloor, margin_floor_pct: publishFloor.margin_floor_pct })}
              disabled={!publishFloor.margin_enabled}
              data-testid="margin-floor-pct"
              className="h-9 w-20 text-sm"
            />
            %
          </label>
          <span className="text-[11px] text-zinc-400">Recommended 25–30% — low enough to let healthy products through, high enough to catch losers.</span>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg p-4 mb-3" data-testid="premium-discount-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="max-w-lg">
            <h3 className="font-semibold text-sm flex items-center gap-1.5"><DollarSign className="w-4 h-4 text-[#C05A45]" /> Printify Premium cost discount</h3>
            <p className="text-xs text-zinc-500 mt-1">Printify Premium gives up to ~20% off most products (more on some). <strong>Leave this at 0 if your costs already reflect Premium</strong> — Printify usually returns your real discounted cost automatically, and the discount varies per product, so a flat % can double-count. Set a flat % ONLY if you've verified the costs shown here are still full list price.</p>
          </div>
          <div className="flex items-center gap-1.5">
            <Input
              type="number" step="1" min="0" max="50"
              value={publishFloor.premium_pct ?? 0}
              onChange={(e) => setPublishFloor({ ...publishFloor, premium_pct: Number(e.target.value) })}
              onBlur={() => savePremium({ ...publishFloor, premium_pct: publishFloor.premium_pct ?? 0 })}
              data-testid="premium-discount-pct"
              className="h-9 w-20 text-sm"
            />
            <span className="text-xs text-zinc-500">% off</span>
          </div>
        </div>
      </div>

      {shopifyConnected && (
        <div className="bg-white border border-zinc-200 rounded-lg p-4 mb-3" data-testid="fix-main-image-card">
          <div className="max-w-lg">
            <h3 className="font-semibold text-sm flex items-center gap-1.5"><ImageIcon className="w-4 h-4 text-[#C05A45]" /> Restore availability &amp; set main image color</h3>
            <p className="text-xs text-zinc-500 mt-1">Sets every live product's variants to <strong>continue selling when out of stock</strong> (so print-on-demand items never show "sold out") and makes the <strong>front view</strong> of your chosen color the main image. <strong>Shopify-only</strong> — it does not re-publish through Printify. New products apply this automatically. Changes flow to <strong>Faire</strong> and <strong>Google</strong> on their next sync.</p>
          </div>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <label className="text-xs text-zinc-600 flex items-center gap-1.5">
              Main image color
              <select
                value={mockupColor}
                onChange={(e) => saveMockupColor(e.target.value)}
                data-testid="mockup-color-select"
                className="h-9 px-2 rounded-md border border-zinc-200 bg-white text-xs focus:outline-none focus:ring-1 focus:ring-[#C05A45]"
              >
                {(colorOptions.length ? colorOptions : [mockupColor]).map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </label>
            <button
              onClick={fixMainImage}
              disabled={!shopifyConnected}
              data-testid="fix-main-image-btn"
              className="h-9 px-3 rounded-md bg-[#C05A45] hover:bg-[#a94a38] disabled:opacity-50 text-white text-xs font-medium flex items-center gap-1.5 transition-colors"
            >
              {fixImg ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />}
              {fixImg ? `Fixing… ${fixImgProgress ? `${fixImgProgress.done}/${fixImgProgress.total}` : ""} · click to resume if stuck` : "Restore availability & fix main image (all live)"}
            </button>
            {fixImg && (
              <button onClick={() => cancelJob("fixImg", jobIds.fixImg, setFixImg)} data-testid="cancel-fix-main-btn"
                className="h-9 px-3 rounded-md border border-zinc-300 text-zinc-600 hover:bg-red-50 hover:text-red-600 hover:border-red-300 text-xs font-medium flex items-center gap-1.5 transition-colors">
                <X className="w-3.5 h-3.5" /> Stop
              </button>
            )}
            <span className="text-[11px] text-zinc-400">If your color isn't offered on a product, it uses the first front view.</span>
          </div>
          <div className="mt-3 pt-3 border-t border-zinc-100">
            <p className="text-xs text-zinc-500 mb-2">Some products still "sold out"? Scan the WHOLE catalog and list only the ones Shopify still treats as sold out (read-only, changes nothing).</p>
            <button
              onClick={runDiagnostic}
              disabled={!shopifyConnected || diagBusy}
              data-testid="diagnose-availability-btn"
              className="h-9 px-3 rounded-md border border-zinc-300 text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 text-xs font-medium flex items-center gap-1.5 transition-colors"
            >
              {diagBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Info className="w-3.5 h-3.5" />}
              {diagBusy ? `Scanning… ${diagProgress ? `${diagProgress.done}/${diagProgress.total}` : ""}` : "Scan for sold-out products (whole catalog)"}
            </button>
            {diag && (
              <div className="mt-3 rounded-md border border-zinc-200 bg-zinc-50 p-3 overflow-x-auto" data-testid="diagnostic-results">
                <p className="text-xs mb-2" data-testid="diagnostic-summary">
                  Scanned <strong>{diag.scanned}</strong> · <strong className="text-emerald-600">{diag.fully_sellable}</strong> sellable · <strong className={diag.problem_count ? "text-red-600" : "text-emerald-600"}>{diag.problem_count}</strong> sold-out
                </p>
                {diag.problem_count === 0 ? (
                  <p className="text-[11px] text-zinc-500">Every product is sellable per Shopify's API. If the storefront still shows "sold out", it's a publish/theme-cache issue (not inventory) — reload the store page, or check the product is published to the Online Store channel.</p>
                ) : (
                  <table className="text-[11px] w-full">
                    <thead className="text-zinc-500">
                      <tr className="text-left">
                        <th className="pr-3 pb-1">Sold-out product</th><th className="pr-3 pb-1">Sellable</th>
                        <th className="pr-3 pb-1">Continue-sell</th><th className="pr-3 pb-1">Why</th><th className="pb-1"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(diag.problems || []).map((r, i) => (
                        <tr key={i} className="border-t border-zinc-200">
                          <td className="pr-3 py-1 max-w-[240px] truncate">{r.title || "—"}</td>
                          <td className="pr-3 py-1 text-red-600 font-medium">{`${r.available_for_sale ?? 0}/${r.variants ?? 0}`}</td>
                          <td className="pr-3 py-1">{`${r.continue_selling ?? 0}/${r.variants ?? 0}`}</td>
                          <td className="pr-3 py-1 text-zinc-500">{r.reason || "—"}</td>
                          <td className="py-1">
                            {r.shopify_id && (
                              <button
                                onClick={() => draftProduct({ productId: r.shopify_id, key: r.shopify_id })}
                                disabled={draftBusy === r.shopify_id}
                                data-testid={`draft-product-${i}`}
                                className="h-6 px-2 rounded border border-zinc-300 text-zinc-600 hover:bg-zinc-100 disabled:opacity-50 text-[10px] font-medium"
                              >
                                {draftBusy === r.shopify_id ? "…" : "→ Draft"}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
          <div className="mt-3 pt-3 border-t border-zinc-100">
            <p className="text-xs text-zinc-500 mb-2">Some products have a broken/missing app↔Shopify link and the repair above can't reach them. This walks your <strong>Shopify catalog directly</strong> and untracks every product so the whole store is permanently sellable — nothing slips through a lost link. Reconnect Shopify first (needs inventory permission).</p>
            <div className="flex items-center gap-2">
              <button
                onClick={makeAllAvailable}
                disabled={!shopifyConnected}
                data-testid="make-all-available-btn"
                className="h-9 px-3 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-medium flex items-center gap-1.5 transition-colors"
              >
                {allAvailBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                {allAvailBusy ? `Untracking… ${allAvailProgress ? `${allAvailProgress.done}/${allAvailProgress.total}` : ""} · click to resume if stuck` : "Make ALL Shopify products available (whole store)"}
              </button>
              {allAvailBusy && (
                <button onClick={() => cancelJob("allAvail", jobIds.allAvail, setAllAvailBusy)} data-testid="cancel-all-avail-btn"
                  className="h-9 px-3 rounded-md border border-zinc-300 text-zinc-600 hover:bg-red-50 hover:text-red-600 hover:border-red-300 text-xs font-medium flex items-center gap-1.5 transition-colors">
                  <X className="w-3.5 h-3.5" /> Stop
                </button>
              )}
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-zinc-100">
            <p className="text-xs text-zinc-500 mb-2">Just one product still "sold out"? Paste its Honorifica.com URL to fix + verify that single product instantly.</p>
            <div className="flex items-center gap-2 flex-wrap">
              <Input
                value={fixUrl}
                onChange={(e) => setFixUrl(e.target.value)}
                placeholder="https://honorifica.com/products/…"
                data-testid="fix-product-url-input"
                className="h-9 text-xs max-w-md"
              />
              <button
                onClick={fixOneProduct}
                disabled={!shopifyConnected || fixOneBusy || !fixUrl.trim()}
                data-testid="fix-product-btn"
                className="h-9 px-3 rounded-md border border-zinc-300 text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 text-xs font-medium flex items-center gap-1.5 transition-colors"
              >
                {fixOneBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                {fixOneBusy ? "Fixing…" : "Check & fix this product"}
              </button>
              <button
                onClick={() => draftProduct({ url: fixUrl.trim(), key: "url" })}
                disabled={!shopifyConnected || draftBusy === "url" || !fixUrl.trim()}
                data-testid="draft-product-url-btn"
                className="h-9 px-3 rounded-md border border-zinc-300 text-zinc-600 hover:bg-zinc-100 disabled:opacity-50 text-xs font-medium flex items-center gap-1.5 transition-colors"
              >
                {draftBusy === "url" ? "…" : "Move to Draft"}
              </button>
            </div>
            {fixOneResult && (
              <div className="text-[11px] mt-2 rounded-md border border-zinc-200 bg-zinc-50 p-2" data-testid="fix-product-result">
                <p><strong>{fixOneResult.title || fixOneResult.handle}</strong> — status <strong>{fixOneResult.status || "?"}</strong> · on Online Store: <strong className={fixOneResult.online_store ? "text-emerald-600" : "text-red-600"}>{fixOneResult.online_store ? "yes" : "NO"}</strong> · sellable <strong className={fixOneResult.fixed ? "text-emerald-600" : "text-red-600"}>{fixOneResult.sellable}/{fixOneResult.total}</strong></p>
                {fixOneResult.fix_error && <p className="text-red-600 mt-1">Update error: {fixOneResult.fix_error}</p>}
                {(fixOneResult.variants || []).length > 0 && (
                  <table className="mt-1 w-full">
                    <thead className="text-zinc-500"><tr className="text-left"><th className="pr-3">Variant</th><th className="pr-3">Available</th><th className="pr-3">Policy</th><th className="pr-3">Tracked</th><th className="pr-3">Qty</th></tr></thead>
                    <tbody>
                      {fixOneResult.variants.map((v, i) => (
                        <tr key={i} className="border-t border-zinc-200">
                          <td className="pr-3 py-0.5 max-w-[160px] truncate">{v.title}</td>
                          <td className={`pr-3 py-0.5 ${v.available ? "text-emerald-600" : "text-red-600 font-medium"}`}>{v.available ? "yes" : "no"}</td>
                          <td className="pr-3 py-0.5">{v.policy || "—"}</td>
                          <td className="pr-3 py-0.5">{v.tracked === true ? "yes" : v.tracked === false ? "no" : "—"}</td>
                          <td className="pr-3 py-0.5">{v.qty ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
          <div className="mt-3 pt-3 border-t border-zinc-100">
            <p className="text-xs text-zinc-500 mb-2">Folded shots still showing on Shopify/Faire? Rebuild galleries with only clean front/back photos (removes folded). Shopify-only, recoverable. Safe to re-run — already-cleaned products are skipped, so it resumes if interrupted. <strong>Only one color showing per product?</strong> Use <strong>"Fix color images"</strong> to re-attach every color's photo to its variants across all products.</p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => removeFolded(false)}
                disabled={!shopifyConnected}
                data-testid="remove-folded-btn"
                className="h-9 px-3 rounded-md border border-[#C05A45] text-[#C05A45] hover:bg-[#fdf3f0] disabled:opacity-50 text-xs font-medium flex items-center gap-1.5 transition-colors"
              >
                {foldedBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />}
                {foldedBusy ? `Cleaning… ${foldedProgress ? `${foldedProgress.done}/${foldedProgress.total}` : ""} · click to resume if stuck` : "Remove folded images from all galleries"}
              </button>
              {!foldedBusy && (
                <button
                  onClick={() => removeFolded(true)}
                  disabled={!shopifyConnected}
                  data-testid="rebuild-galleries-force-btn"
                  className="h-9 px-3 rounded-md border border-amber-500 text-amber-700 hover:bg-amber-50 disabled:opacity-50 text-xs font-medium flex items-center gap-1.5 transition-colors"
                  title="Re-attach each color's photo to its variants on ALL products (fixes 'only white shows')"
                >
                  <RefreshCw className="w-3.5 h-3.5" /> Fix color images (re-apply to all)
                </button>
              )}
              {!foldedBusy && (
                <button
                  onClick={reattachSingle}
                  disabled={!shopifyConnected || singleBusy}
                  data-testid="reattach-single-photos-btn"
                  className="h-9 px-3 rounded-md border border-sky-600 text-sky-700 hover:bg-sky-50 disabled:opacity-50 text-xs font-medium flex items-center gap-1.5 transition-colors"
                  title="Attach the front photo to every model/size variant of phone cases, mugs, posters, stickers"
                >
                  {singleBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />}
                  {singleBusy ? `Attaching… ${singleProgress ? `${singleProgress.done}/${singleProgress.total}` : ""}` : "Fix missing photos (phone cases, mugs, posters…)"}
                </button>
              )}
              {foldedBusy && (
                <button onClick={() => cancelJob("folded", jobIds.folded, setFoldedBusy)} data-testid="cancel-folded-btn"
                  className="h-9 px-3 rounded-md border border-zinc-300 text-zinc-600 hover:bg-red-50 hover:text-red-600 hover:border-red-300 text-xs font-medium flex items-center gap-1.5 transition-colors">
                  <X className="w-3.5 h-3.5" /> Stop
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="flex items-start gap-2 mb-5 rounded-lg bg-blue-50/60 border border-blue-100 p-3" data-testid="spoke-help">
        <Info className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
        <p className="text-[11px] text-blue-800/80 leading-relaxed">Heartline connects directly to <b>Printify</b> and <b>Shopify</b> only. TikTok Shop, Etsy, and Faire are enabled inside your Shopify admin via their official sales-channel apps — Shopify then fans your products out to each marketplace.</p>
      </div>

      <div className="mb-5 rounded-lg border border-zinc-200 bg-white p-4" data-testid="shorten-titles-card">
        <div className="text-sm font-semibold flex items-center gap-1.5 text-zinc-800"><Tag className="w-4 h-4" /> Shorten long product titles</div>
        <p className="text-[11px] text-zinc-500 mt-1 mb-2.5 leading-relaxed">Turns long SEO titles into clean storefront names (e.g. <em>"Military Mom Strong Tote Bag"</em>) and re-syncs them to Shopify — better for RangeMe &amp; Faire imports. Title only: no republish, no image/inventory change. The full SEO copy stays in the description. New products are already built with short titles.</p>
        <div className="flex items-center gap-2">
          <button
            onClick={shortenTitles}
            disabled={titleBusy}
            data-testid="shorten-titles-btn"
            className="h-9 px-3 rounded-md border border-blue-500 text-blue-600 hover:bg-blue-50 disabled:opacity-50 text-xs font-medium flex items-center gap-1.5 transition-colors"
          >
            {titleBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Tag className="w-3.5 h-3.5" />}
            {titleBusy ? `Shortening… ${titleProgress ? `${titleProgress.done}/${titleProgress.total}` : ""}` : "Shorten all long titles"}
          </button>
        </div>
      </div>

      <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50/40 p-4" data-testid="sanitize-claims-card">
        <div className="text-sm font-semibold flex items-center gap-1.5 text-zinc-800"><ShieldCheck className="w-4 h-4 text-amber-600" /> Remove false "hand drawn / handmade" claims</div>
        <p className="text-[11px] text-zinc-500 mt-1 mb-2.5 leading-relaxed">Your designs are digital/AI print-on-demand, so wording like <em>"hand drawn"</em>, <em>"hand painted"</em> or <em>"handmade"</em> is a false-advertising risk. This scans every product and swaps that wording for <em>"original" / "specially designed"</em> — in Heartline <strong>and</strong> on the live Shopify listing. Description text only: no republish, no image/inventory change. New products are already generated without these claims.</p>
        <div className="flex items-center gap-2">
          <button
            onClick={sanitizeClaims}
            disabled={claimsBusy}
            data-testid="sanitize-claims-btn"
            className="h-9 px-3 rounded-md border border-amber-500 text-amber-700 hover:bg-amber-100 disabled:opacity-50 text-xs font-medium flex items-center gap-1.5 transition-colors"
          >
            {claimsBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
            {claimsBusy ? `Cleaning… ${claimsProgress ? `${claimsProgress.done}/${claimsProgress.total}` : ""}` : "Remove false-craft claims"}
          </button>
          <button
            onClick={syncPrintifyText}
            disabled={textBusy}
            data-testid="sync-printify-text-btn"
            title="Copy the live Shopify title + description into Printify and Heartline so republishing can't bring back old wording"
            className="h-9 px-3 rounded-md border border-zinc-400 text-zinc-700 hover:bg-zinc-100 disabled:opacity-50 text-xs font-medium flex items-center gap-1.5 transition-colors"
          >
            {textBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            {textBusy ? `Syncing… ${textProgress ? `${textProgress.done}/${textProgress.total}` : ""}` : "Sync Shopify text → Printify"}
          </button>
        </div>
      </div>

      <div className="mb-5 rounded-lg border border-sky-200 bg-sky-50/40 p-4" data-testid="walmart-id-card">
        <div className="text-sm font-semibold flex items-center gap-1.5 text-zinc-800"><ShieldCheck className="w-4 h-4 text-sky-600" /> Walmart GTIN exemption — stamp "CUSTOM" identifier</div>
        <p className="text-[11px] text-zinc-500 mt-1 mb-2.5 leading-relaxed">Got a Walmart GTIN exemption? Walmart wants <em>"CUSTOM"</em> as the product identifier so listings don't hang. This stamps a dedicated metafield <code className="text-[10px] bg-sky-100 px-1 rounded">custom.walmart_product_id = CUSTOM</code> on every Shopify-linked product. Then map it <strong>once</strong> in Marketplace Connect → <strong>Attribute Mode → Product Identifiers → Product ID</strong> (type GTIN). Kept off the barcode field so Google Shopping stays clean. New products get it automatically on publish.</p>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={stampWalmartId}
            disabled={walmartBusy}
            data-testid="stamp-walmart-id-btn"
            className="h-9 px-3 rounded-md border border-sky-500 text-sky-700 hover:bg-sky-100 disabled:opacity-50 text-xs font-medium flex items-center gap-1.5 transition-colors"
          >
            {walmartBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
            {walmartBusy ? `Stamping… ${walmartProgress ? `${walmartProgress.done}/${walmartProgress.total}` : ""}` : "1. Stamp Walmart \"CUSTOM\" ID on all products"}
          </button>
          <button
            onClick={stampApparelAttrs}
            disabled={apparelBusy}
            data-testid="stamp-apparel-attrs-btn"
            className="h-9 px-3 rounded-md border border-sky-500 text-sky-700 hover:bg-sky-100 disabled:opacity-50 text-xs font-medium flex items-center gap-1.5 transition-colors"
          >
            {apparelBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Tag className="w-3.5 h-3.5" />}
            {apparelBusy ? `Stamping… ${apparelProgress ? `${apparelProgress.done}/${apparelProgress.total}` : ""}` : "2. Stamp apparel attributes (tees/hoodies)"}
          </button>
        </div>
        <p className="text-[11px] text-zinc-400 mt-2">After stamping, map these once in Marketplace Connect → Category Specific Attributes → T-Shirts: age group, fabric content, neck/sleeve style, color category, etc. → the matching <code className="text-[10px] bg-sky-100 px-1 rounded">walmart.*</code> metafield.</p>
      </div>

      <button onClick={save} disabled={saving} data-testid="save-distribution-btn" className="h-10 px-4 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium flex items-center gap-1.5">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save distribution settings
      </button>
    </div>
  );
}
